import { MEDIA_QUERIES } from 'design'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useCallManager } from '../../contexts/call_manager_context'
import usePreviousProps from '../../hooks/previous_props_hook'
import { useMediaQuery } from '../../hooks/use_media_query'
import {
  COLLECTION_MUTED_EVENT,
  WIDGET_LAYOUT,
  collectionScrollBox,
  dialogElement,
  iframeElements,
  isFirstInCollection,
} from './constants'

export const useWidgetLayout = ({ collectionReady }: any) => {
  const [widgetLayout, setWidgetLayout] =
    useState<WIDGET_LAYOUT>(getInitialLayout)
  const [positionInCollection, setPositionInCollection] = useState()
  const prevPositionInCollection = usePreviousProps(positionInCollection)
  const [collectionFullscreen, setCollectionIsFullscreen] = useState(false)
  const prevCollectionFullscreen = usePreviousProps(collectionFullscreen)
  const desktop = useMediaQuery(MEDIA_QUERIES.md, window.parent)
  const [collectionMuted, setCollectionMuted] = useState(
    fullscreen ? true : widgetLayout !== WIDGET_LAYOUT.FULLSCREEN,
  )
  const {
    activeCall: { isOnCall },
  } = useCallManager()
  const prevCarouselScrollLeft = useRef()

  useEffect(() => {
    const onChange = (event: CustomEvent) => {
      if (event.detail.hasOwnProperty('position')) {
        setPositionInCollection(event.detail.position)
      }
      if (event.detail.isFullscreen !== undefined) {
        setCollectionIsFullscreen(event.detail.isFullscreen)
      }
    }
    window.addEventListener(POSITION_IN_COLLECTION_EVENT, onChange)

    return () => {
      window.removeEventListener(POSITION_IN_COLLECTION_EVENT, onChange)
    }
  }, [])

  useEffect(() => {
    const onChange = (event: CustomEvent) => {
      setCollectionMuted(event.detail)
    }
    window.addEventListener(COLLECTION_MUTED_EVENT, onChange)

    return () => {
      window.removeEventListener(COLLECTION_MUTED_EVENT, onChange)
    }
  }, [])

  const toggleLayout = useCallback(
    (layout: WIDGET_LAYOUT, options = {}) => {
      const { isSelectedInCollection } = {
        isSelectedInCollection: true,
        ...options,
      }

      setWidgetLayout((prevLayout) => {
        if (prevLayout === layout) {
          switch (prevLayout) {
            case WIDGET_LAYOUT.FULLSCREEN:
              if (collection && isSelectedInCollection) {
                for (let i = 0; i < iframeElements.length; i++) {
                  const iframe = iframeElements[i]
                  iframe.contentWindow.dispatchEvent(
                    new CustomEvent(POSITION_IN_COLLECTION_EVENT, {
                      detail: {
                        ...(type === 'embedded' || type === 'contact_form'
                          ? {}
                          : { position: undefined }),
                        isFullscreen: false,
                      },
                    }),
                  )
                }
                return prevLayout
              } else {
                closeModal()
              }
              switch (type) {
                case 'floating':
                  return WIDGET_LAYOUT.FLOATING
                case 'embedded':
                  return WIDGET_LAYOUT.EMBEDDED
                case 'widget':
                  return desktop
                    ? WIDGET_LAYOUT.WIDGET
                    : WIDGET_LAYOUT.WIDGET_MINIMISED
                case 'contact_form':
                  return WIDGET_LAYOUT.CONTACT_FORM
                default:
                  return WIDGET_LAYOUT.EMBEDDED
              }
            case WIDGET_LAYOUT.WIDGET_MINIMISED:
              if (desktop) {
                return WIDGET_LAYOUT.WIDGET
              } else {
                showModal()
                if (collection && isSelectedInCollection) {
                  notifyOtherIframesOfPositions()
                }
                return WIDGET_LAYOUT.FULLSCREEN
              }
            case WIDGET_LAYOUT.WIDGET_MAXIMISED:
              return WIDGET_LAYOUT.FULLSCREEN
            default:
              return WIDGET_LAYOUT.EMBEDDED
          }
        } else {
          if (layout === WIDGET_LAYOUT.FULLSCREEN) {
            if (collection && isSelectedInCollection) {
              if (type === 'carousel') {
                prevCarouselScrollLeft.current = collectionScrollBox.scrollLeft
              }
              notifyOtherIframesOfPositions()
              return prevLayout
            } else {
              showModal()
            }
          }
          return layout
        }
      })
    },
    [desktop],
  )

  useEffect(() => {
    if (fullscreen && collection && isFirstInCollection && collectionReady) {
      toggleLayout(WIDGET_LAYOUT.FULLSCREEN)
    }
  }, [collectionReady, toggleLayout])

  useEffect(() => {
    if (collectionFullscreen && widgetLayout !== WIDGET_LAYOUT.FULLSCREEN) {
      toggleLayout(WIDGET_LAYOUT.FULLSCREEN, { isSelectedInCollection: false })
    }
  }, [collectionFullscreen, positionInCollection, toggleLayout, widgetLayout])

  useEffect(() => {
    if (!collectionFullscreen && prevCollectionFullscreen) {
      toggleLayout(WIDGET_LAYOUT.FULLSCREEN, { isSelectedInCollection: false })
    }
  }, [
    collectionFullscreen,
    positionInCollection,
    prevCollectionFullscreen,
    prevPositionInCollection,
    toggleLayout,
  ])

  useEffect(() => {
    if (!desktop && widgetLayout === WIDGET_LAYOUT.WIDGET) {
      setWidgetLayout(WIDGET_LAYOUT.FULLSCREEN)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desktop])

  useEffect(() => {
    if (!isOnCall && widgetLayout === WIDGET_LAYOUT.WIDGET_MAXIMISED) {
      setWidgetLayout(WIDGET_LAYOUT.FULLSCREEN)
    }
  }, [isOnCall, widgetLayout])

  return {
    widgetLayout,
    setWidgetLayout,
    toggleLayout,
    isSelectedInCollection: positionInCollection === 0,
    positionInCollection,
    collectionFullscreen,
    collectionMuted,
    prevCarouselScrollLeft,
  }
}

// eslint-disable-next-line
// @ts-ignore
const { type, fullscreen, collection } = window.speakeasySettings || {}

const getInitialLayout = () => {
  if (fullscreen && !collection) {
    showModal()
    return WIDGET_LAYOUT.FULLSCREEN
  }
  switch (type) {
    case 'floating':
      return WIDGET_LAYOUT.FLOATING
    case 'embedded':
      return WIDGET_LAYOUT.EMBEDDED
    case 'widget':
      return WIDGET_LAYOUT.WIDGET_MINIMISED
    case 'contact_form':
      return WIDGET_LAYOUT.CONTACT_FORM
    case 'carousel':
      return WIDGET_LAYOUT.EMBEDDED
    default:
      return WIDGET_LAYOUT.EMBEDDED
  }
}

export const POSITION_IN_COLLECTION_EVENT = 'POSITION_IN_COLLECTION'

const showModal = () => {
  const dialog = dialogElement
  dialog.open = false
  dialog.showModal()
}

const closeModal = () => {
  const dialog = dialogElement
  dialog.close()
  dialog.open = true
}

const notifyOtherIframesOfPositions = () => {
  const indexOfSelected = iframeElements.indexOf(window.frameElement)
  dialogElement.hidden = true
  setTimeout(() => {
    // hide, then show later in order to hide layout shift
    dialogElement.hidden = false
  }, 100)

  for (let i = 0; i < iframeElements.length; i++) {
    setTimeout(
      () => {
        iframeElements[i].contentWindow.dispatchEvent(
          new CustomEvent(POSITION_IN_COLLECTION_EVENT, {
            detail: {
              ...((type === 'embedded' || type === 'contact_form') &&
              !fullscreen
                ? {}
                : { position: indexOfSelected * -1 + i }),
              isFullscreen: true,
            },
          }),
        )
      },
      i ? 10 : 0, // give first iframe a head start, as it has listeners for the collection that need to be cleaned up first
    )
  }
}
